import AppContext from 'App/AppContext';
import { IPDefaultButton, IPDropdown, IPLabel, IPPrimaryButton } from 'baseComponents';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import TextInfo from 'components/Notification/Info';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import { getProcessingActivityRoleOptions } from 'components/ProcessingActivity/ProcessingActivityOptions';
import {
  globalLayerRightSideBarProps,
  globalStackStylesPanel,
  globalStackStylesPanelFooter,
  globalStackTokensGapLarge,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Joi from 'joi';
import ProcessingActivity, { ProcessingActivityRole } from 'models/processingActivity';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'utils/string';

interface IEditProcessingActivityPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  processingActivityToEdit: ProcessingActivity;
  onEditProcessingActivity: (activity: ProcessingActivity) => void;
  onCheckCode: (code: string) => Promise<string>;
  isLoading: boolean;
}

export const EditProcessingActivityPanel = (props: IEditProcessingActivityPanelProps) => {
  const { t } = useTranslation(['translation', 'processingActivities']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Joi.ValidationError | undefined>(undefined);
  const [errorAsync, setErrorAsync] = useState<boolean>(false);
  const [processingActivityToEdit, setProcessingActivityToEdit] = useState<ProcessingActivity>(
    props.processingActivityToEdit,
  );
  const appContext = useContext(AppContext);

  useEffect(() => {
    setProcessingActivityToEdit(props.processingActivityToEdit);
  }, [props.processingActivityToEdit]);

  const disableSave = (): boolean => {
    return !hasChanged() || error || errorAsync || props.isLoading ? isLoading || true : false;
  };

  const onSave = (editAfterSave: boolean): void => {
    if (isLoading) return;

    setIsLoading(true);
    props.onEditProcessingActivity(processingActivityToEdit);
    setIsLoading(false);

    props.onDismiss();
  };

  const hasChanged = (): boolean => {
    return !props.processingActivityToEdit.isEqual(processingActivityToEdit);
  };

  const _localizedFields: Record<string, string> = {
    name: t('processingActivities:Panels.Edit.NameLabel'),
    code: t('processingActivities:Panels.Edit.CodeLabel'),
  };

  const validate = async (fieldName: string): Promise<string> => {
    if (hasChanged()) {
      const result = processingActivityToEdit.validate(_localizedFields);
      setError(result.error);
      if (result.error) {
        const fieldError = result.error.details.find((e) => {
          return e.context?.key === fieldName;
        });

        if (fieldError) {
          return fieldError.message;
        }
      }

      if (fieldName === 'code' && processingActivityToEdit.code) {
        const newCode = await props.onCheckCode(processingActivityToEdit.code);
        const exists = !isEmpty(newCode);
        setErrorAsync(exists);

        if (exists) {
          return t('processingActivities:Panels.Edit.CodeError');
        }
      }
    }

    return '';
  };

  // On Render Footer: Shows Save and Cancel Button at the bottom and performs appropriate actions
  const onRenderFooterContent = () => {
    return (
      <IPStack horizontal wrap styles={globalStackStylesPanelFooter} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPrimaryButton
            disabled={disableSave()}
            onClick={() => {
              onSave(false);
              props.onDismiss();
            }}
            text={t('translation:General.Button.Save')}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton onClick={props.onDismiss} text={t('translation:General.Button.Cancel')}></IPDefaultButton>
        </IPStack.Item>
      </IPStack>
    );
  };

  return (
    <IPPanel
      headerText={t('processingActivities:Panels.Edit.Title')}
      type={IPPanelType.medium}
      isOpen={props.isOpen}
      isBlocking={true}
      isLightDismiss={true}
      layerProps={globalLayerRightSideBarProps}
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      onDismiss={props.onDismiss}
    >
      <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapLarge}>
        <IPStack.Item>
          <IPTextField
            required
            value={processingActivityToEdit.code}
            label={t('processingActivities:Panels.Edit.CodeLabel')}
            placeholder={t('processingActivities:Panels.Edit.CodePlaceholder')}
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
              const processingActivityClone = processingActivityToEdit.clone();
              processingActivityClone.code = newValue ? newValue.trim() : '';
              setProcessingActivityToEdit(processingActivityClone);
            }}
            onGetErrorMessage={() => {
              return validate('code');
            }}
            maxLength={32}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPTextField
            required
            autoFocus={!appContext.isMobileView}
            label={t('processingActivities:Panels.Edit.NameLabel')}
            placeholder={t('processingActivities:Panels.Edit.NamePlaceholder')}
            value={processingActivityToEdit.name}
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
              const processingActivityClone = processingActivityToEdit.clone();
              processingActivityClone.name = newValue ? newValue.trimStart() : '';
              setProcessingActivityToEdit(processingActivityClone);
            }}
            onGetErrorMessage={() => {
              return validate('name');
            }}
            maxLength={512}
          />
        </IPStack.Item>
        <IPStack.Item>
          <TextInfo message={t('processingActivities:Panels.Edit.RoleInfo')}>
            <IPLabel>{t('processingActivities:Panels.Edit.Role')}</IPLabel>
          </TextInfo>
          <IPDropdown
            selectedKey={processingActivityToEdit.role}
            options={getProcessingActivityRoleOptions()}
            onChange={(ev, option) => {
              const processingActivityClone = processingActivityToEdit.clone();
              processingActivityClone.role = option?.key as ProcessingActivityRole;
              setProcessingActivityToEdit(processingActivityClone);
            }}
          />
        </IPStack.Item>
        <IPStack.Item>
          <AuthSchemaPicker
            selectedAuthSchemaId={processingActivityToEdit.authSchemaId}
            onSelect={(schema) => {
              const newProcessingActivityToAdd = processingActivityToEdit.clone();
              newProcessingActivityToAdd.authSchemaId = schema?.authSchemaId;
              setProcessingActivityToEdit(newProcessingActivityToAdd);
            }}
          />
        </IPStack.Item>
      </IPStack>
    </IPPanel>
  );
};
